<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto py-8"
        @click.self="$emit('update:modelValue', false)"
      >
        <div class="bg-neutral-100 rounded-xl w-full max-w-6xl mx-4 relative">
          <button
            class="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
            @click="$emit('update:modelValue', false)"
          >
            <i class="pi pi-times text-xl"></i>
          </button>

          <div class="flex justify-center items-center gap-8 py-6">
            <img :src="globeIcon" alt="Globe" class="w-28 h-28" />
            <div class="flex flex-col md:items-center">
              <h2 class="text-4xl font-bold text-[#1a233f]">{{ t('langModal.title') }}</h2>
              <p class="text-gray-500 mt-1">{{ t('langModal.chooseLocation') }}</p>
            </div>
          </div>

          <div class="px-6 pb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            <div v-for="region in regions" :key="region.name">
              <h3 class="font-bold text-[#1a233f] mb-4 uppercase tracking-wide">
                {{ region.name }}
              </h3>
              <ul class="space-y-2">
                <li v-for="country in region.countries" :key="country.name">
                  <button
                    class="flex items-center gap-3 w-full text-left px-3 py-2 rounded-lg transition-colors hover:bg-gray-50"
                    :class="{ 'border border-[#2db9d6] bg-[#f0fbfd]': isActive(country) }"
                    @click="select(country)"
                  >
                    <img
                      :src="getFlagUrl(country.flag)"
                      :alt="country.name"
                      class="w-8 h-auto rounded-sm flex-shrink-0"
                    />
                    <div>
                      <div class="text font-medium text-[#1a233f]">{{ country.name }}</div>
                      <div class="text-sm text-gray-400">{{ country.lang }}</div>
                    </div>
                    <i v-if="isActive(country)" class="pi pi-check text-[#2db9d6] ml-auto"></i>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useLocaleStore } from '@/stores/general/localeStore'
import { regions } from '@/i18n/regions'
import { watch } from 'vue'
import { getFlagUrl } from '@/utils/helpers'
import { useRouter, useRoute } from 'vue-router'

const globeIcon = new URL('@/assets/images/general/globe.svg', import.meta.url).href

const props = defineProps({ modelValue: Boolean })
const emit = defineEmits(['update:modelValue'])

const { t, locale } = useI18n({ useScope: 'global' })
const localeStore = useLocaleStore()
const router = useRouter()
const route = useRoute()

function isActive(country) {
  return locale.value === country.locale && localeStore.country === country.name
}

function select(country) {
  localeStore.set(country.flag, country.name)
  localStorage.setItem('locale', country.locale)
  localStorage.setItem('localeFlag', country.flag)
  localStorage.setItem('localeCountry', country.name)
  localStorage.setItem('localeStoredAt', String(Date.now()))
  router.push({
    name: route.name,
    params: { ...route.params, locale: country.locale },
    query: route.query,
  })
  emit('update:modelValue', false)
}

watch(
  () => props.modelValue,
  (val) => {
    document.body.style.overflow = val ? 'hidden' : ''
  }
)
</script>

<script>
export default { inheritAttrs: false }
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
